import React, { useState, useRef, useEffect } from "react";
import Banner from "../../ui/Banner/Banner";
import "./Login.css";
import { MultiFactorResolver, TotpMultiFactorGenerator } from "firebase/auth";
import Button from "react-bootstrap/Button";
import landinglogo from "../../../assets/images/landinglogo.svg";
// 1. Import the arrow icon
import { BsArrowLeft } from "react-icons/bs";

interface MfaVerifyPageProps {
  resolver: MultiFactorResolver | null;
  onVerificationComplete: () => void;
  onCancel: () => void;
}

const MfaVerifyPage: React.FC<MfaVerifyPageProps> = ({
  resolver,
  onVerificationComplete,
  onCancel,
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [error, setError] = useState<string>("");
  const [verifying, setVerifying] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const BackIcon = BsArrowLeft as any;

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    const code = otp.join("");
    if (code.length === 6) {
      handleVerifyMfa(undefined, code);
    }
  }, [otp]);

  const handleVerifyMfa = async (e?: React.FormEvent, codeOverride?: string) => {
    if (e) e.preventDefault();

    const currentCode = codeOverride || otp.join("");

    if (!resolver) {
      setError("MFA resolver not available.");
      return;
    }

    if (currentCode.length !== 6) {
      if (e) setError("Please enter the full 6-digit code.");
      return;
    }

    setVerifying(true);
    setError("");

    try {
      const totpHint = resolver.hints.find(
        (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );

      if (!totpHint) {
        setError("No authenticator found.");
        return;
      }

      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        currentCode
      );

      await resolver.resolveSignIn(assertion);
      onVerificationComplete();
    } catch (err: any) {
      setError("Invalid verification code. Please try again.");
      console.error("MFA Verification Error:", err);
      setVerifying(false);
    }
  };

  const handleChange = (element: HTMLInputElement, index: number) => {
    if (isNaN(Number(element.value))) return false;
    const newOtp = [...otp];
    newOtp[index] = element.value.substring(element.value.length - 1);
    setOtp(newOtp);
    if (element.value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6).split("");
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      pastedData.forEach((val, i) => {
        if (i < 6 && !isNaN(Number(val))) newOtp[i] = val;
      });
      setOtp(newOtp);
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  return (
    <div className="login_card position-relative">
      {/* 2. Back Arrow placed absolutely at top-left */}
      <div
        className="flex gap-2 justify-start items-center align-middle w-[80px] hover:underline mb-2"
        style={{ cursor: "pointer", color: "#6c757d" }}
        onClick={onCancel}
        title="Back to Login"
      >
        <BackIcon size={24} /> Back
      </div>

      <div className="login_card_logo mt-4">
        <img src={landinglogo} alt="landinglogo" />
      </div>
      <div className="login_head">
        <h1>Verify 2-Step Verification</h1>
        <p>Enter the verification code from your authenticator app.</p>
      </div>

      {error && (
        <div className="mt-4">
          <Banner dismissBanner={() => setError("")} message={error} status="error" />
        </div>
      )}

      <form onSubmit={(e) => handleVerifyMfa(e)} className="w-100">
        <div className="login_filled_flx mb-4" style={{ justifyContent: 'center' }}>
          <div className="d-flex gap-2 justify-content-center w-100 my-3">
            {otp.map((data, index) => {
              return (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  value={data}
                  ref={(el) => (inputRefs.current[index] = el)}
                  onChange={(e) => handleChange(e.target, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  disabled={verifying}
                  className="form-control text-center p-0"
                  style={{
                    width: "45px",
                    height: "50px",
                    fontSize: "20px",
                    borderRadius: "8px",
                    border: "1px solid #ced4da",
                    backgroundColor: "#fff",
                    fontWeight: "600",
                    color: "#495057"
                  }}
                  autoComplete="off"
                  inputMode="numeric"
                />
              );
            })}
          </div>

          {/* Buttons */}
          <div className="space-y-5">
            <button
              type="submit"
              disabled={verifying}
              className="w-full bg-[#7E57C2] text-white
                         hover:bg-[#6b46ac]
                         rounded-[10px] py-[14px] px-[22px]
                         text-[16px] font-medium
                         disabled:opacity-70"
            >
              {verifying ? "Verifying…" : "Verify Code"}
            </button>

            <button
              type="button"
              onClick={onCancel}
              style={{ border: "1px solid #7e57c2" }}
              className="block w-full text-center text-[#7e57c2] hover:bg-[#F9F6FF] !rounded-[10px]
                !py-[14px] !px-[22px] text-base no-underline cursor-pointer !text-[16px] !font-medium"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* 3. Single Verify Button (Cancel removed) */}
        <div className="login_btn mby_50">
          <Button
            type="submit"
            variant="primary"
            disabled={verifying || otp.join("").length < 6}
            className="w-100"
          >
            {verifying ? "Verifying..." : "Verify"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MfaVerifyPage;
